import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, Landmark, ArrowLeft, Map as MapIcon, Sparkles, LifeBuoy, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Pill } from "../components/common";
import Breadcrumbs from "../components/Breadcrumbs";
import { MissionCard, MissionMap, Disclaimer, LINK_LABELS } from "./VisasHub";
import { OfficialBadge, LastVerified, SuggestCorrection, PublicNotes, AdminNotesPanel } from "../components/VisaWidgets";
import VisaFeaturedArticle from "../components/VisaFeaturedArticle";
import LastCheckedBadge from "../components/LastCheckedBadge";
import { getFeaturedArticles } from "../data/visaFeaturedArticles";
import { useAuth } from "../context/AuthContext";
import { SITE } from "../lib/site";
import api from "../lib/api";

export default function VisaCountryDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isMod = user && ["moderator", "admin"].includes(user.role);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false); setShowMap(false);
    api.get(`/visas/countries/${id}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.name} Passport, Visa, Embassy & Consulate Information | ${SITE.brandDomain}`;
    }).catch(() => setNotFound(true));
  }, [id]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data: article } = await api.post(`/visas/countries/${data.id}/generate-article?force=true`);
      setData((d) => ({ ...d, article }));
      toast.success("Country guide generated");
    } catch { toast.error("Could not generate the guide"); }
    finally { setGenerating(false); }
  };

  if (notFound) return (
    <div className="max-w-7xl mx-auto px-5 py-20 text-center" data-testid="visa-country-not-found">
      <p className="text-muted-foreground">Country not found in the visa directory yet.</p>
      <Link to="/visas" className="text-primary font-700 hover:underline mt-2 inline-block">← Back to Passports & Visas</Link>
    </div>
  );
  if (!data) return <Spinner full />;

  const article = data.article;
  const hasLinks = Object.keys(data.links || {}).length > 0;
  const featuredArticles = getFeaturedArticles(data.id);

  return (
    <div data-testid={`visa-country-page-${data.id}`}>
      <PageHeader eyebrow={`${SITE.name} · Passports & Visas · ${data.region}`} title={data.name} entityCode={data.iso2}
        subtitle={data.summary}>
        <Breadcrumbs className="mt-4 text-zinc-300" items={[
          { label: "Visas & Embassies", to: "/visas" },
          { label: data.region, to: "/visas" },
          { label: data.name },
        ]} />
        <div className="flex flex-wrap gap-1.5 mt-4">
          {(data.entry_programs || []).map((p) => <Pill key={p} tone="green">{p}</Pill>)}
          {data.schengen && <Pill tone="blue">Schengen Area</Pill>}
        </div>
      </PageHeader>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 space-y-12">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/visas" data-testid="back-to-visas" className="inline-flex items-center gap-1.5 text-sm text-primary font-700 hover:underline">
            <ArrowLeft className="w-4 h-4" /> All countries, visas &amp; embassies
          </Link>
          {isMod && (
            <button onClick={generate} disabled={generating} data-testid="generate-article-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-violet-400 text-violet-700 text-sm font-700 hover:bg-violet-50 disabled:opacity-50">
              <Sparkles className="w-4 h-4" /> {generating ? "Generating…" : article ? "Regenerate guide" : "Generate country guide"}
            </button>
          )}
        </div>

        <PublicNotes notes={data.public_notes} />

        <section data-testid="official-links-section">
          <h2 className="font-heading font-700 text-lg md:text-lg mb-1 flex items-center gap-2">
            <Landmark className="w-5 h-5 text-[#0e7490]" /> Official Visa &amp; Immigration Links
          </h2>
          <p className="text-sm text-muted-foreground mb-4">Capital: {data.capital} · Always verify requirements on these official websites before travel.</p>
          {hasLinks ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(data.links).map(([key, url]) => (
                <a key={key} href={url} target="_blank" rel="noreferrer" data-testid={`official-link-${key}`}
                  className="border border-zinc-300 bg-white px-5 py-4 hover:border-[#0e7490] transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="font-700 text-sm">{LINK_LABELS[key] || key}</span>
                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">{url.replace(/^https?:\/\//, "")}</div>
                  <div className="mt-1.5"><OfficialBadge /></div>
                </a>
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-amber-300 bg-amber-50/50 p-5 text-sm text-amber-800" data-testid="links-not-verified">
              <span className="font-700">Information Not Yet Verified.</span> Official government links for {data.name} are being
              researched and verified by our editors. Meanwhile, contact the nearest embassy or consulate of {data.name} listed below.
            </div>
          )}
          <div className="mt-3"><LastVerified date={data.last_verified} source={data.source_org} /></div>
          {data.last_verified && (
            <LastCheckedBadge
              className="mt-3"
              lastVerified={data.last_verified}
              sourceOrg={data.source_org || "Official government websites"}
              sourceUrl={Object.values(data.links || {})[0]}
              reviewDays={180}
            />
          )}
        </section>

        {featuredArticles.length > 0 && (
          <section className="space-y-6" data-testid="visa-featured-articles-section">
            {featuredArticles.map((a) => (
              <VisaFeaturedArticle key={a.id} article={a} />
            ))}
          </section>
        )}

        {article && (
          <section data-testid="country-article">
            <div className="border border-zinc-300 bg-white p-6 sm:p-10">
              <div className="text-xs font-mono uppercase tracking-widest text-[#0e7490] mb-2">{SITE.name} Country Guide</div>
              <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight mb-4">{article.title}</h2>
              {article.overview && article.overview.split("\n\n").map((p, i) => (
                <p key={i} className="text-base text-zinc-700 leading-relaxed mb-3">{p}</p>
              ))}
              <div className="space-y-6 mt-6">
                {(article.sections || []).map((s) => (
                  <div key={s.heading} data-testid={`article-section-${s.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <h3 className="font-heading font-700 text-lg md:text-lg mb-2 border-b border-zinc-200 pb-1.5">{s.heading}</h3>
                    {s.body.split("\n\n").map((p, i) => <p key={i} className="text-base text-zinc-700 leading-relaxed mb-2">{p}</p>)}
                  </div>
                ))}
              </div>
              {article.faq?.length > 0 && (
                <div className="mt-8" data-testid="article-faq">
                  <h3 className="font-heading font-700 text-lg md:text-lg mb-3 flex items-center gap-2"><HelpCircle className="w-5 h-5 text-[#0e7490]" /> Frequently Asked Questions</h3>
                  <div className="space-y-3">
                    {article.faq.map((f) => (
                      <div key={f.q} className="border border-zinc-200 p-4">
                        <div className="font-700 text-sm mb-1">{f.q}</div>
                        <p className="text-sm text-zinc-700">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-6 border-t border-zinc-100 pt-3">
                {article.editorial_note} Generated {new Date(article.generated_at).toLocaleDateString()} — sources &amp; verification: see official links above.
              </p>
            </div>
          </section>
        )}

        {data.missions_in_country?.length > 0 && (
          <section data-testid="missions-in-country-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-heading font-700 text-lg md:text-lg">Foreign embassies &amp; consulates in {data.name}</h2>
              <button onClick={() => setShowMap((s) => !s)} data-testid="country-map-toggle"
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-700 border transition-colors ${showMap ? "bg-[#0e7490] border-[#0e7490] text-white" : "bg-white border-zinc-300 text-zinc-700"}`}>
                <MapIcon className="w-3.5 h-3.5" /> {showMap ? "Hide map" : "Show on map"}
              </button>
            </div>
            {showMap && <div className="mb-5"><MissionMap missions={data.missions_in_country} height="380px" /></div>}
            <div className="grid sm:grid-cols-2 gap-4">
              {data.missions_in_country.map((m) => <MissionCard key={m.id} m={m} />)}
            </div>
          </section>
        )}

        {data.missions_abroad?.length > 0 && (
          <section data-testid="missions-abroad-section">
            <h2 className="font-heading font-700 text-lg md:text-lg mb-4">{data.name} embassies &amp; consulates around the world</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.missions_abroad.map((m) => <MissionCard key={m.id} m={m} />)}
            </div>
          </section>
        )}

        <section className="border border-zinc-300 bg-white p-6" data-testid="help-while-abroad">
          <h2 className="font-heading font-700 text-lg md:text-lg mb-2 flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-[#0e7490]" /> Help While Abroad</h2>
          <p className="text-sm text-zinc-700 mb-3">
            If you experience a lost or stolen passport, arrest, serious illness, accident or other emergency while abroad,
            contact <span className="font-700">your own country's</span> nearest embassy or consulate — they provide emergency
            travel documents and citizen assistance. Use the <Link to="/visas" className="text-primary font-700 hover:underline">Embassy &amp; Consulate Finder</Link> to
            locate the right mission, and check your government's travel advisory service before departure.
          </p>
          {data.links?.advisory && (
            <a href={data.links.advisory} target="_blank" rel="noreferrer" className="text-sm text-primary font-700 hover:underline inline-flex items-center gap-1">
              {data.name} travel advisories <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </section>

        <SuggestCorrection entityType="country" entityId={data.id} entityName={data.name} />
        <AdminNotesPanel entityType="country" entityId={data.id} />

        <Disclaimer text={data.disclaimer} />
      </div>
    </div>
  );
}
